import { authorizedRequest, request } from "../../../services/apiClient";
import type { UserProfilePhoto } from "@/types/auth";
import { unwrapArrayResponse } from "../../../utils/apiHelpers";
import type { UpdatePayload } from "@/types/crud";

export interface Workspace {
  id: string;
  name: string;
  owner_id: string;
  description?: string | null;
  created_at: string;
  updated_at: string;
  archived_at?: string | null;
  archived_by?: string | null;
  role?: "owner" | "editor" | "viewer";
  agg_active_lots: number;
  agg_computed_area_m2: number;
  agg_traverse_length_m: number;
}

export interface WorkspaceMemberUser {
  id: string;
  email: string;
  name: string;
  profile_photo?: UserProfilePhoto | null;
}

export interface WorkspaceMember {
  workspace_id: string;
  user_id: string;
  role: string;
  joined_at: string;
  user?: WorkspaceMemberUser;
}

export interface WorkspaceMemberListResponse {
  members: WorkspaceMember[];
}

export interface WorkspaceInvitation {
  id: string;
  workspace_id: string;
  email: string;
  role: string;
  status: string;
  invited_by: string;
  expires_at: string;
  created_at: string;
  accepted_at?: string | null;
  revoked_at?: string | null;
  revoked_by?: string | null;
  last_reminded_at?: string | null;
}

export interface WorkspaceInvitationListResponse {
  invitations: WorkspaceInvitation[];
}

export interface WorkspaceInvitationEnvelope {
  invitation: WorkspaceInvitation;
}

export interface WorkspaceInvitationAcceptanceResponse {
  status: string;
  workspace: Workspace;
  invitation: WorkspaceInvitation;
}

export interface WorkspaceInvitationPreview {
  invitation: WorkspaceInvitation;
  workspace_name: string;
  workspace_description?: string | null;
  inviter_name: string;
  inviter_email?: string;
  expires_in_seconds?: number | null;
  expired_for_seconds?: number | null;
}

export interface CreateWorkspacePayload {
  name: string;
  description?: string | null;
}

export type UpdateWorkspacePayload = UpdatePayload<CreateWorkspacePayload>;

export async function listWorkspaces(
  accessToken: string
): Promise<Workspace[]> {
  return authorizedRequest<Workspace[]>(accessToken, "/workspaces");
}

export async function getWorkspace(
  accessToken: string,
  workspaceId: string
): Promise<Workspace> {
  return authorizedRequest<Workspace>(
    accessToken,
    `/workspaces/${workspaceId}`
  );
}

export async function createWorkspace(
  accessToken: string,
  payload: CreateWorkspacePayload
): Promise<Workspace> {
  return authorizedRequest<Workspace>(accessToken, "/workspaces", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export async function updateWorkspace(
  accessToken: string,
  workspaceId: string,
  payload: UpdateWorkspacePayload
): Promise<Workspace> {
  return authorizedRequest<Workspace>(
    accessToken,
    `/workspaces/${workspaceId}`,
    {
      method: "PUT",
      body: JSON.stringify(payload)
    }
  );
}

export async function archiveWorkspace(
  accessToken: string,
  workspaceId: string
): Promise<Workspace> {
  return authorizedRequest<Workspace>(
    accessToken,
    `/workspaces/${workspaceId}/archive`,
    { method: "POST" }
  );
}

export async function restoreWorkspace(
  accessToken: string,
  workspaceId: string
): Promise<Workspace> {
  return authorizedRequest<Workspace>(
    accessToken,
    `/workspaces/${workspaceId}/restore`,
    { method: "POST" }
  );
}

export async function listWorkspaceMembers(
  accessToken: string,
  workspaceId: string
): Promise<WorkspaceMember[]> {
  const response = await authorizedRequest<
    WorkspaceMember[] | WorkspaceMemberListResponse
  >(accessToken, `/workspaces/${workspaceId}/members`);
  return unwrapArrayResponse<WorkspaceMember>(response, "members");
}

export async function removeWorkspaceMember(
  accessToken: string,
  workspaceId: string,
  userId: string
): Promise<void> {
  await authorizedRequest(accessToken, `/workspaces/${workspaceId}/members/${userId}`, {
    method: "DELETE"
  });
}

export async function listWorkspaceInvitations(
  accessToken: string,
  workspaceId: string
): Promise<WorkspaceInvitation[]> {
  const response = await authorizedRequest<WorkspaceInvitationListResponse>(
    accessToken,
    `/workspaces/${workspaceId}/invites`
  );
  return unwrapArrayResponse<WorkspaceInvitation>(response, "invitations");
}

export async function createWorkspaceInvitation(
  accessToken: string,
  workspaceId: string,
  email: string
): Promise<WorkspaceInvitation> {
  const payload = await authorizedRequest<WorkspaceInvitationEnvelope>(
    accessToken,
    `/workspaces/${workspaceId}/invites`,
    {
      method: "POST",
      body: JSON.stringify({ email })
    }
  );
  return payload.invitation;
}

export async function resendWorkspaceInvitation(
  accessToken: string,
  workspaceId: string,
  invitationId: string
): Promise<WorkspaceInvitation> {
  const payload = await authorizedRequest<WorkspaceInvitationEnvelope>(
    accessToken,
    `/workspaces/${workspaceId}/invites/${invitationId}/resend`,
    {
      method: "POST"
    }
  );
  return payload.invitation;
}

export async function revokeWorkspaceInvitation(
  accessToken: string,
  workspaceId: string,
  invitationId: string
): Promise<WorkspaceInvitation> {
  const payload = await authorizedRequest<WorkspaceInvitationEnvelope>(
    accessToken,
    `/workspaces/${workspaceId}/invites/${invitationId}`,
    {
      method: "DELETE"
    }
  );
  return payload.invitation;
}

export async function acceptWorkspaceInvitation(
  accessToken: string,
  token: string
): Promise<WorkspaceInvitationAcceptanceResponse> {
  return authorizedRequest<WorkspaceInvitationAcceptanceResponse>(
    accessToken,
    `/workspaces/invitations/${token}`,
    {
      method: "POST"
    }
  );
}

export async function getWorkspaceInvitationPreview(
  token: string
): Promise<WorkspaceInvitationPreview> {
  return request<WorkspaceInvitationPreview>(
    `/workspaces/invitations/${encodeURIComponent(token)}`
  );
}

export async function declineWorkspaceInvitation(
  accessToken: string,
  token: string
): Promise<WorkspaceInvitation> {
  const response = await authorizedRequest<WorkspaceInvitationEnvelope>(
    accessToken,
    `/workspaces/invitations/${encodeURIComponent(token)}/decline`,
    { method: "POST" }
  );
  return response.invitation;
}
